import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import Box from '@mui/material/Box';
import styled from '@mui/material/styles/styled';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import visuallyHidden from '@mui/utils/visuallyHidden';

import * as Styles from './Table.styles';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    padding: '8px 16px',
  },
  [`&.${tableCellClasses.body}`]: {
    padding: '8px 16px',
  },
}));

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead({
  order,
  orderBy,
  onRequestSort,
  headCells,
  classNameHeadRow,
}) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow className={classNameHeadRow}>
        {headCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            className={classNames(headCell.className, 'font-bold')}
          >
            {headCell.isSort ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {headCell.tooltip}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              <>
                {headCell.label}
                {headCell.tooltip}
              </>
            )}
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable({
  rows,
  headCells,
  defaultOrderBy,
  noBorderStyle,
  isResponsive = false,
  breakpointStyle1 = 'lg',
  breakpointStyle2 = '768',
  useOddEven = false,
  onHeaderClick,
  className = '',
  stickyHeader = false,
  stickyFirstCol = false,
  stickyFirstRow = false,
  id = undefined,
  tableContainerId = undefined,
  groupData = '',
}) {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [selected, setSelected] = useState([]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    if (onHeaderClick) {
      onHeaderClick({
        order: isAsc ? 'desc' : 'asc',
        orderBy: property,
      });
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.name);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const renderBodyContent = (row, prevRow, index) => {
    if (groupData) {
      const currentDate = `${row[groupData].slice(6, 8)}/${row[groupData].slice(
        4,
        6
      )}/${row[groupData].slice(0, 4)}`;
      let prevDate = null;
      if (prevRow) {
        prevDate = `${prevRow?.[groupData]?.slice(6, 8)}/${row?.[
          groupData
        ]?.slice(4, 6)}/${row?.[groupData]?.slice(0, 4)}`;
      }
      if (currentDate !== prevDate) {
        return (
          <React.Fragment key={row?.id || index}>
            <TableRow
              className={classNames(
                stickyFirstCol ? 'stickyFirstCol' : '',
                stickyFirstRow ? 'stickyFirstRow' : ''
              )}
            >
              <StyledTableCell colSpan={headCells.length}>
                <span className="font-bold">{currentDate}</span>
              </StyledTableCell>
            </TableRow>
            <TableRow
              className={classNames(
                stickyFirstCol ? 'stickyFirstCol' : '',
                stickyFirstRow ? 'stickyFirstRow' : '',
                row?.className
              )}
            >
              {headCells?.map((head) => (
                <StyledTableCell
                  key={head.id}
                  data-label={`${head.label}:`}
                  align={head.numeric ? 'right' : 'left'}
                >
                  {row[head.id]}
                </StyledTableCell>
              ))}
            </TableRow>
          </React.Fragment>
        );
      }
    }
    return (
      <TableRow
        key={row?.id || index}
        className={classNames(
          stickyFirstCol ? 'stickyFirstCol' : '',
          stickyFirstRow ? 'stickyFirstRow' : '',
          row?.className
        )}
      >
        {headCells?.map((head) => (
          <StyledTableCell
            key={head.id}
            data-label={`${head.label}:`}
            align={head.numeric ? 'right' : 'left'}
          >
            {row[head.id]}
          </StyledTableCell>
        ))}
      </TableRow>
    );
  };

  return (
    <Styles.Wrapper
      className={classNames(
        noBorderStyle ? 'no-style' : '',
        isResponsive ? 'table-responsive' : '',
        useOddEven ? 'odd-even' : '',
        className
      )}
      $breakpointResponsive={breakpointStyle1}
      $breakpointResponsive2={breakpointStyle2}
    >
      <Paper>
        <TableContainer id={tableContainerId}>
          <Table
            aria-labelledby="tableTitle"
            size="medium"
            stickyHeader={stickyHeader}
            id={id}
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows?.length || 0}
              headCells={headCells}
              classNameHeadRow={classNames(
                stickyFirstCol ? 'stickyFirstCol' : ''
              )}
            />
            <TableBody
              className={classNames(stickyFirstCol ? 'stickyFirstCol' : '')}
            >
              {rows?.map((row, index) =>
                renderBodyContent(row, rows[index - 1], index)
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Styles.Wrapper>
  );
}
